import { OdinIcon } from '@myosh/odin-components';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ExportRecordsBackgroundJobApiProps,
  type FileBackgroundJobDetails,
  JobStatus,
  JobType,
} from '../../redux/slices/background-job-slice';
import { downloadFileFromUrl } from '../../utils/file-download';
import type { BackgroundJobPanelItemProps } from './background-job-panel.component';
import BaseJobPanelItem from './base-job-panel-item.component';

function FileBackgroundJobItem({
  job,
  onRemove,
}: BackgroundJobPanelItemProps<ExportRecordsBackgroundJobApiProps, FileBackgroundJobDetails>) {
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    const { fileName, presignedUrl } = job.details || {};

    if (job.status !== JobStatus.Success || !fileName || !presignedUrl) {
      return;
    }

    downloadFileFromUrl(presignedUrl, fileName);
  }, [job.details, job.status]);

  const getFileIcon = useCallback(
    (format: string) => {
      if (job.type === JobType.ExportFullRecords) {
        return <OdinIcon icon="FileArchive" />;
      } else if (format === 'PDF') {
        return <OdinIcon icon="FileText" />;
      } else if (format === 'EXCEL' || format === 'TEMPLATE' || format === 'TEMPLATE_AND_RECORDS') {
        return <OdinIcon icon="FileSpreadsheet" />;
      } else {
        return <OdinIcon icon="FileText" />;
      }
    },
    [job.type]
  );

  const jobSubtitle = useMemo(() => {
    if (job.type === JobType.ExportSingleRecord) {
      return t('record-export');
    } else if (job.type === JobType.ExportFullRecords) {
      return t('export-full-records-data');
    } else if (job.type === JobType.ExportRecords) {
      return t('export-records-list');
    }
  }, [job.type, t]);

  return (
    <BaseJobPanelItem
      title={job?.details?.fileName ?? job.apiProps.formName ?? 'No file name available'}
      subtitle={jobSubtitle}
      status={job.status}
      onRemove={onRemove}
      onClick={handleClick}
      icon={getFileIcon(job.apiProps.format)}
    />
  );
}

export default React.memo(FileBackgroundJobItem);
